import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardTitle, Metric } from '../components/UI';
import RouteMap from '../components/RouteMap';
import { getPublicPlan } from '../api';
import { Loader2, Link as LinkIcon, ExternalLink } from 'lucide-react';

export default function PublicPlan() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setData(await getPublicPlan(id));
      } catch (e) {
        setError('This plan does not exist or has been removed.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      window.prompt('Copy this link:', window.location.href);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--lf-bg)' }}>
      <header
        className="px-6 py-4 flex items-center justify-between"
        style={{ background: 'var(--lf-navy)', color: '#fff' }}
      >
        <Link to="/" className="text-xl font-extrabold no-underline" style={{ color: '#fff' }}>
          Lindore <span style={{ color: '#60a5fa' }}>FreightAI</span>
        </Link>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
          style={{ background: 'var(--lf-green-bg)', color: 'var(--lf-green)' }}
        >
          Shared plan
        </span>
      </header>

      <main className="max-w-[1100px] mx-auto p-6 md:p-8">
        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--lf-muted)' }}>
            <Loader2 className="animate-spin" size={14} /> Loading plan…
          </div>
        ) : error ? (
          <Card>
            <p className="text-sm text-red-600">{error}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium no-underline"
              style={{ color: 'var(--lf-blue)' }}
            >
              Go to dashboard <ExternalLink size={13} />
            </Link>
          </Card>
        ) : (
          <div className="space-y-[18px]">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold m-0">
                  Recommended plan{' '}
                  {data.shipment_code && (
                    <span style={{ color: 'var(--lf-muted)' }}>· {data.shipment_code}</span>
                  )}
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--lf-muted)' }}>
                  {data.lane}
                </p>
              </div>
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg"
                style={{
                  background: '#eef2f7',
                  color: '#344054',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <LinkIcon size={14} /> {copied ? 'Link copied!' : 'Copy link'}
              </button>
            </div>

            <Card>
              <div className="flex items-center justify-between mb-2">
                <b className="text-sm">Score</b>
                <b className="text-sm" style={{ color: 'var(--lf-green)' }}>
                  {data.optimization.score} / 100
                </b>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden mb-4"
                style={{ background: '#e9edf4' }}
              >
                <div
                  className="lf-score-bar block h-full"
                  style={{
                    background: 'var(--lf-green)',
                    width: `${data.optimization.score}%`,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Metric label="Cost" value={`$${data.optimization.cost.toLocaleString()}`} />
                <Metric label="Fuel" value={`$${data.optimization.fuel.toLocaleString()}`} />
                <Metric label="Miles" value={data.optimization.miles.toLocaleString()} />
                <Metric label="Duration" value={data.optimization.duration} />
              </div>
              <p className="leading-relaxed text-sm" style={{ color: '#475467' }}>
                <b>Why:</b> {data.optimization.explanation}
              </p>
            </Card>

            <Card>
              <CardTitle>Route</CardTitle>
              <RouteMap
                origin={data.optimization.origin}
                destination={data.optimization.destination}
                stops={data.optimization.stops || []}
                height={360}
              />
            </Card>

            <p className="text-center text-xs mt-2" style={{ color: 'var(--lf-muted)' }}>
              Shared via Lindore FreightAI. Read-only.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
